// Singleton instance to be shared across components
export let manualTermsServiceInstance = null;

/**
 * Initialize the manual terms service singleton
 * @param {Object} termsStore - The terms store
 * @param {Object} claimsStore - The claims store
 * @param {Object} specStore - The spec store
 * @param {Object} persistentTermsService - The persistent terms service (optional)
 * @returns {ManualTermsService} - The initialized service instance
 */
export function initializeManualTermsService(termsStore, claimsStore, specStore, persistentTermsService) {
    if (!manualTermsServiceInstance) {
        manualTermsServiceInstance = new ManualTermsService(termsStore, claimsStore, specStore, persistentTermsService);
    }
    return manualTermsServiceInstance;
}

/**
 * Service to handle manual term selection from editors
 */
export class ManualTermsService {
    constructor(termsStore, claimsStore, specStore, persistentTermsService) {
        this.termsStore = termsStore;
        this.claimsStore = claimsStore;
        this.specStore = specStore;
        this.persistentTermsService = persistentTermsService;
        this.unsubscribe = null;
        this.claimsEditor = null;
        this.specEditor = null;
        this.claimsMouseUpHandler = null;
        this.specMouseUpHandler = null;
        this.selectionTimeout = null;
        
        this.setupStoreSubscription();
    }

    /**
     * Set up subscription to terms store to detect mode changes
     */
    setupStoreSubscription() {
        console.log('[ManualTermsService] Setting up store subscription');
        this.unsubscribe = this.termsStore.subscribe(store => {
            // Only log when the mode actually changes
            if (store.isTermAddingMode !== this.lastTermAddingMode) {
                console.log('[ManualTermsService] Term adding mode changed:', store.isTermAddingMode);
                this.lastTermAddingMode = store.isTermAddingMode;
            }
            // When term adding mode changes, enable or disable handlers
            if (store.isTermAddingMode) {
                this.enableTermSelection();
            } else {
                this.disableTermSelection();
            }
        });
    }

    /**
     * Enable selection handler for the Claims editor only
     */
    enableTermSelection() {
        // Only enable if not already enabled
        if (!this.isEnabled) {
            console.log('[ManualTermsService] Enabling term selection mode');
            document.body.classList.add('term-adding-mode');
            this.isEnabled = true;
            
            // Setup handler for Claims editor only
            this.setupClaimsEditorSelection();
        }
    }

    /**
     * Disable selection handlers
     */
    disableTermSelection() {
        // Only disable if currently enabled
        if (this.isEnabled) {
            console.log('[ManualTermsService] Disabling term selection mode');
            document.body.classList.remove('term-adding-mode');
            this.isEnabled = false;
            
            this.cleanupClaimsEditorSelection();
        }
    }

    /**
     * Set up selection handling for the Claims Editor (CodeMirror)
     */
    setupClaimsEditorSelection() {
        console.log('[ManualTermsService] Setting up Claims editor selection');
        
        // Check if handler already exists
        if (this.claimsMouseUpHandler) {
            console.log('[ManualTermsService] WARNING: Claims handler already exists, cleaning up first');
            this.cleanupClaimsEditorSelection();
        }
        
        // Handle mouse up events to capture selection
        this.claimsMouseUpHandler = this.debounce(() => {
            console.log('[ManualTermsService] Mouse up event triggered');
            
            // Check if we're still in term adding mode (safety check)
            if (!document.body.classList.contains('term-adding-mode')) {
                console.log('[ManualTermsService] Not in term adding mode, ignoring event');
                return;
            }
            
            // Get current window selection
            const selection = window.getSelection();
            console.log('[ManualTermsService] Selection object:', {
                isCollapsed: selection?.isCollapsed,
                rangeCount: selection?.rangeCount,
                selectedText: selection?.toString()
            });
            
            if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
                console.log('[ManualTermsService] No valid selection, returning');
                return;
            }
            
            // Find the claims editor element
            const claimsEditorElement = document.querySelector('.claims-editor');
            console.log('[ManualTermsService] Claims editor element found:', !!claimsEditorElement);
            if (!claimsEditorElement) {
                console.log('[ManualTermsService] Claims editor not found');
                return;
            }
            
            // Check if selection is inside the claims editor
            let node = selection.anchorNode;
            let isInClaimsEditor = false;
            let nodeTraversal = [];
            
            while (node && !isInClaimsEditor) {
                nodeTraversal.push({
                    nodeName: node.nodeName,
                    className: node.className || 'no-class',
                    isClaimsEditor: node === claimsEditorElement,
                    hasClaimsClass: node.classList?.contains('claims-editor')
                });
                
                if (node === claimsEditorElement || node.classList?.contains('claims-editor')) {
                    isInClaimsEditor = true;
                }
                node = node.parentNode;
            }
            
            console.log('[ManualTermsService] Node traversal:', nodeTraversal);
            console.log('[ManualTermsService] Is in claims editor:', isInClaimsEditor);
            
            // If selection is in claims editor, process it
            if (isInClaimsEditor) {
                const selectedText = selection.toString();
                console.log('[ManualTermsService] Claims selection:', selectedText);
                
                if (selectedText && selectedText.trim()) {
                    this.processSelectedTerm(selectedText);
                }
            } else {
                console.log('[ManualTermsService] Selection not in claims editor');
            }
        }, 300);
        
        // Add event listener to the document to capture all selections
        console.log('[ManualTermsService] Adding mouseup event listener to document');
        document.addEventListener('mouseup', this.claimsMouseUpHandler);
    }

    /**
     * Set up selection handling for the Spec Editor (TipTap)
     */
    setupSpecEditorSelection() {
        // Handle mouse up events to capture selection
        this.specMouseUpHandler = this.debounce(() => {
            // Get current window selection
            const selection = window.getSelection();
            if (!selection || selection.isCollapsed || selection.rangeCount === 0) return;
            
            // Find the spec editor element
            const specEditorElement = document.querySelector('.spec-editor');
            if (!specEditorElement) return;
            
            // Check if selection is inside the spec editor
            let node = selection.anchorNode;
            let isInSpecEditor = false;
            
            while (node && !isInSpecEditor) {
                if (node === specEditorElement || node.classList?.contains('spec-editor') || 
                    node.classList?.contains('ProseMirror')) {
                    isInSpecEditor = true;
                }
                node = node.parentNode;
            }
            
            // If selection is in spec editor, process it
            if (isInSpecEditor) {
                const selectedText = selection.toString();
                console.log('[ManualTermsService] Spec selection:', selectedText);
                
                if (selectedText && selectedText.trim()) {
                    this.processSelectedTerm(selectedText);
                }
            }
        }, 300);
        
        // Add event listener to the document to capture all selections
        document.addEventListener('mouseup', this.specMouseUpHandler);
    }

    /**
     * Clean up event handlers for Claims Editor
     */
    cleanupClaimsEditorSelection() {
        // console.log('[ManualTermsService] Cleaning up Claims editor selection');
        if (this.claimsMouseUpHandler) {
            console.log('[ManualTermsService] Removing mouseup event listener');
            document.removeEventListener('mouseup', this.claimsMouseUpHandler);
            this.claimsMouseUpHandler = null;
        } else {
            // console.log('[ManualTermsService] No Claims handler to clean up');
        }
        
        // Clear any pending debounced timeouts
        if (this.selectionTimeout) {
            console.log('[ManualTermsService] Clearing pending selection timeout');
            clearTimeout(this.selectionTimeout);
            this.selectionTimeout = null;
        }
    }

    /**
     * Clean up event handlers for Spec Editor
     */
    cleanupSpecEditorSelection() {
        if (this.specMouseUpHandler) {
            document.removeEventListener('mouseup', this.specMouseUpHandler);
            this.specMouseUpHandler = null;
        }
    }

    /**
     * Process selected text to extract a clean term
     * @param {string} text - The text selected by the user
     */
    processSelectedTerm(text) {
        console.log('[ManualTermsService] Processing selected term, raw text:', JSON.stringify(text));
        
        if (!text || typeof text !== 'string') {
            console.log('[ManualTermsService] Invalid text input:', text);
            return;
        }
        
        // Clean up the selected text
        let term = text.trim();
        console.log('[ManualTermsService] After trim:', JSON.stringify(term));
        
        // Additional processing:
        // 1. Remove punctuation at the beginning and end
        term = term.replace(/^[^\w]+|[^\w]+$/g, '');
        console.log('[ManualTermsService] After punctuation removal:', JSON.stringify(term));
        
        // 2. Make sure term is not empty after processing
        if (term.length === 0) {
            console.log('[ManualTermsService] Rejected term - empty after processing:', term);
            return;
        }
        
        // 3. Make sure it's at least 2 characters long
        if (term.length < 2) {
            console.log('[ManualTermsService] Rejected term - too short:', term);
            return;
        }
        
        console.log('[ManualTermsService] Processed term - VALID:', JSON.stringify(term));
        
        // Show confirmation dialog
        this.showConfirmationDialog(term);
    }

    /**
     * Process any existing selection in the claims editor
     * @returns {boolean} - True if a valid selection was processed, false otherwise
     */
    processExistingSelection() {
        console.log('[ManualTermsService] Checking for existing selection');
        
        // Get current window selection
        const selection = window.getSelection();
        console.log('[ManualTermsService] Selection object:', {
            isCollapsed: selection?.isCollapsed,
            rangeCount: selection?.rangeCount,
            selectedText: selection?.toString()
        });
        
        if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
            console.log('[ManualTermsService] No valid selection');
            return false;
        }
        
        // Find the claims editor element
        const claimsEditorElement = document.querySelector('.claims-editor');
        console.log('[ManualTermsService] Claims editor element found:', !!claimsEditorElement);
        if (!claimsEditorElement) {
            console.log('[ManualTermsService] Claims editor not found');
            return false;
        }
        
        // Check if selection is inside the claims editor
        let node = selection.anchorNode;
        let isInClaimsEditor = false;
        
        while (node && !isInClaimsEditor) {
            if (node === claimsEditorElement || node.classList?.contains('claims-editor')) {
                isInClaimsEditor = true;
            }
            node = node.parentNode;
        }
        
        console.log('[ManualTermsService] Is in claims editor:', isInClaimsEditor);
        
        // If selection is in claims editor, process it
        if (isInClaimsEditor) {
            const selectedText = selection.toString();
            console.log('[ManualTermsService] Claims selection:', selectedText);
            
            if (selectedText && selectedText.trim()) {
                this.processSelectedTerm(selectedText);
                return true;
            }
        }
        
        console.log('[ManualTermsService] No valid selection in claims editor');
        return false;
    }
    
    /**
     * Show a confirmation dialog for the selected term
     * @param {string} term - The term to confirm
     */
    showConfirmationDialog(term) {
        console.log('[ManualTermsService] Showing confirmation dialog for term:', JSON.stringify(term));
        
        // Check if there's already a confirmation dialog
        let confirmDialog = document.getElementById('term-confirmation-dialog');
        console.log('[ManualTermsService] Existing dialog found:', !!confirmDialog);
        
        // If there is an existing dialog, remove it first
        if (confirmDialog) {
            document.body.removeChild(confirmDialog);
        }
        
        // Create dialog element
        confirmDialog = document.createElement('div');
        confirmDialog.id = 'term-confirmation-dialog';
        confirmDialog.className = 'term-confirmation-dialog';
        
        // Calculate smart positioning with flip logic
        const selection = window.getSelection();
        if (selection && !selection.isCollapsed && selection.rangeCount > 0) {
            const range = selection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            
            // Dialog dimensions (approximate)
            const dialogWidth = 280;
            const dialogHeight = 150; // Approximate height
            const gap = 10;
            
            // Get viewport dimensions
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            
            // Calculate initial position (below selection)
            let posX = rect.left;
            let posY = rect.bottom + gap;
            let flipVertical = false;
            
            // Check vertical space
            if (posY + dialogHeight > viewportHeight) {
                // Not enough space below, flip to above
                posY = rect.top - gap;
                flipVertical = true;
            }
            
            // Check horizontal space
            if (posX + dialogWidth > viewportWidth) {
                // Dialog would overflow right edge
                posX = Math.max(10, viewportWidth - dialogWidth - 10);
            }
            
            // Ensure dialog doesn't go off left edge
            posX = Math.max(10, posX);
            
            // Apply positioning
            confirmDialog.style.position = 'fixed';
            confirmDialog.style.top = `${posY}px`;
            confirmDialog.style.left = `${posX}px`;
            
            // If flipped vertically, add transform to align bottom of dialog with selection
            if (flipVertical) {
                confirmDialog.style.transform = 'translateY(-100%)';
            }
            
            // Add data attribute for CSS styling if needed
            confirmDialog.setAttribute('data-flipped', flipVertical ? 'true' : 'false');
        }
        
        // Create dialog content
        confirmDialog.innerHTML = `
            <div class="dialog-content">
                <div class="dialog-header">Confirm new term</div>
                <div class="dialog-body">
                    <div class="term-preview">${term}</div>
                    <div class="dialog-buttons">
                        <button id="confirm-term-btn" class="confirm-btn">Add Term</button>
                        <button id="cancel-term-btn" class="cancel-btn">Cancel</button>
                    </div>
                </div>
            </div>
        `;
        
        // Add styles to the dialog directly - no need to add a style tag to the document
        confirmDialog.style.background = 'var(--color-dialog-bg-warning)'; // Warning background
        confirmDialog.style.borderRadius = '8px';
        confirmDialog.style.boxShadow = 'var(--shadow-lg)';
        confirmDialog.style.border = '1px solid var(--color-term-border-loading)'; // Subtle blue border
        confirmDialog.style.zIndex = '1000';
        confirmDialog.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
        confirmDialog.style.overflow = 'hidden';
        confirmDialog.style.maxWidth = '280px';
        
        // Add a class to the dialog
        confirmDialog.className = 'term-confirmation-dialog';
        
        // Add a stylesheet if it doesn't exist
        if (!document.getElementById('term-dialog-stylesheet')) {
            const style = document.createElement('style');
            style.id = 'term-dialog-stylesheet';
            style.textContent = `
                .term-confirmation-dialog .dialog-content {
                    padding: 0;
                }
                .term-confirmation-dialog .dialog-header {
                    background: var(--color-dialog-header-terms);
                    color: white;
                    padding: 10px 14px;
                    font-weight: 500;
                    font-size: 14px;
                    border-top-left-radius: 8px;
                    border-top-right-radius: 8px;
                }
                .term-confirmation-dialog .dialog-body {
                    padding: 14px;
                    background: var(--color-dialog-bg-warning);
                }
                .term-confirmation-dialog .term-preview {
                    background: var(--color-dialog-preview-bg);
                    border-left: 3px solid var(--color-dialog-header-terms);
                    border-radius: 4px;
                    padding: 10px 12px;
                    margin-bottom: 14px;
                    font-size: 14px;
                    font-weight: 500;
                    color: var(--color-text-primary);
                    word-break: break-word;
                    box-shadow: var(--shadow-sm);
                }
                .term-confirmation-dialog .dialog-buttons {
                    display: flex;
                    justify-content: flex-end;
                    gap: 8px;
                }
                .term-confirmation-dialog .confirm-btn, 
                .term-confirmation-dialog .cancel-btn {
                    padding: 8px 14px;
                    border: none;
                    border-radius: 6px;
                    font-size: 14px;
                    font-weight: 500;
                    cursor: pointer;
                    transition: all 0.2s ease;
                }
                .term-confirmation-dialog .confirm-btn {
                    background: var(--color-dialog-btn-confirm);
                    color: white;
                    box-shadow: var(--shadow-sm);
                }
                .term-confirmation-dialog .confirm-btn:hover {
                    background: var(--color-dialog-btn-confirm-hover);
                    transform: translateY(-1px);
                    box-shadow: var(--shadow-md);
                }
                .term-confirmation-dialog .cancel-btn {
                    background: var(--color-dialog-btn-cancel);
                    color: var(--color-text-primary);
                    box-shadow: var(--shadow-sm);
                }
                .term-confirmation-dialog .cancel-btn:hover {
                    background: var(--color-dialog-btn-cancel-hover);
                    transform: translateY(-1px);
                    box-shadow: var(--shadow-md);
                }
                /* Animation for dialog appearance */
                @keyframes dialogFadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(5px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .term-confirmation-dialog {
                    animation: dialogFadeIn 0.15s ease-out;
                }
                .term-confirmation-dialog[data-flipped="true"] {
                    animation: dialogFadeInFlipped 0.15s ease-out;
                }
                @keyframes dialogFadeInFlipped {
                    from {
                        opacity: 0;
                        transform: translateY(calc(-100% - 5px));
                    }
                    to {
                        opacity: 1;
                        transform: translateY(-100%);
                    }
                }
            `;
            document.head.appendChild(style);
        }
        document.body.appendChild(confirmDialog);
        
        // After adding to DOM, refine positioning based on actual dialog dimensions
        if (selection && !selection.isCollapsed && selection.rangeCount > 0) {
            const dialogRect = confirmDialog.getBoundingClientRect();
            const actualDialogHeight = dialogRect.height;
            const range = selection.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            
            // Re-check vertical positioning with actual height
            let posY = parseFloat(confirmDialog.style.top);
            const flipVertical = confirmDialog.getAttribute('data-flipped') === 'true';
            
            if (!flipVertical && posY + actualDialogHeight > window.innerHeight) {
                // We need to flip after all
                posY = rect.top - 10;
                confirmDialog.style.top = `${posY}px`;
                confirmDialog.style.transform = 'translateY(-100%)';
                confirmDialog.setAttribute('data-flipped', 'true');
            }
        }
        
        // Add event listeners for buttons
        document.getElementById('confirm-term-btn').addEventListener('click', async () => {
            console.log('[ManualTermsService] Confirm button clicked, adding term:', JSON.stringify(term));
            
            // Get current claims text
            let claimsText = '';
            const unsubscribe = this.claimsStore.subscribe(store => {
                claimsText = store.content;
            });
            unsubscribe(); // Immediately unsubscribe after getting the value
            
            // 1. Add as temporary term to show immediately in UI with loading indicator
            this.termsStore.addTemporaryTerm(term, claimsText);
            console.log('[ManualTermsService] Added temporary term for immediate UI feedback');
            
            // 2. Close dialog immediately for snappy UI
            this.cleanupConfirmationDialog(confirmDialog);
            
            // 3. Use PersistentTermsService to add the term permanently in background
            if (this.persistentTermsService) {
                // Don't await - let it run in background
                this.persistentTermsService.addManualTerm(term, claimsText)
                    .then(() => {
                        console.log('[ManualTermsService] Term added successfully via PersistentTermsService');
                    })
                    .catch(error => {
                        console.error('[ManualTermsService] Error adding term via PersistentTermsService:', error);
                        // Update the temporary term to show error state
                        this.termsStore.update(store => {
                            const newTermEntries = new Map(store.termEntries);
                            for (const [_key, entry] of newTermEntries) {
                                if (entry.temporary && entry.parent_term === term) {
                                    entry.loading = false;
                                    entry.error = true;
                                    entry.loadingMessage = 'Failed to add term';
                                    break;
                                }
                            }
                            return { ...store, termEntries: newTermEntries };
                        });
                    });
            } else {
                console.warn('[ManualTermsService] No PersistentTermsService available');
                // Still track in manualTerms for refresh processing
                this.termsStore.addManualTerm(term);
            }
        });
        
        document.getElementById('cancel-term-btn').addEventListener('click', () => {
            console.log('[ManualTermsService] Cancel button clicked');
            this.cleanupConfirmationDialog(confirmDialog);
        });
        
        // Close dialog when clicking outside
        this.handleOutsideClick = (e) => {
            if (confirmDialog && document.body.contains(confirmDialog) && !confirmDialog.contains(e.target)) {
                console.log('[ManualTermsService] Outside click detected, closing dialog');
                this.cleanupConfirmationDialog(confirmDialog);
            }
        };
        document.addEventListener('mousedown', this.handleOutsideClick);
        
        // Track initial scroll position and selection bounds
        const claimsEditor = document.querySelector('.cm-editor .cm-scroller');
        let initialScrollTop = claimsEditor ? claimsEditor.scrollTop : 0;
        let initialWindowScrollY = window.scrollY;
        const initialSelectionRect = selection && !selection.isCollapsed && selection.rangeCount > 0 
            ? selection.getRangeAt(0).getBoundingClientRect() 
            : null;
        
        // Update dialog position on scroll to keep it anchored to the original text position
        this.handleScroll = () => {
            if (!confirmDialog || !document.body.contains(confirmDialog) || !initialSelectionRect) return;
            
            // Calculate scroll delta
            const currentScrollTop = claimsEditor ? claimsEditor.scrollTop : 0;
            const currentWindowScrollY = window.scrollY;
            const scrollDeltaY = (currentScrollTop - initialScrollTop) + (currentWindowScrollY - initialWindowScrollY);
            
            // Get original position values
            const flipVertical = confirmDialog.getAttribute('data-flipped') === 'true';
            const gap = 10;
            
            // Calculate new position based on scroll
            let newPosY;
            if (flipVertical) {
                // Dialog is above selection
                newPosY = initialSelectionRect.top - gap - scrollDeltaY;
            } else {
                // Dialog is below selection
                newPosY = initialSelectionRect.bottom + gap - scrollDeltaY;
            }
            
            // Update dialog position
            confirmDialog.style.top = `${newPosY}px`;
        };
        
        // Listen for scroll on both the claims editor and window
        if (claimsEditor) {
            claimsEditor.addEventListener('scroll', this.handleScroll, { passive: true });
        }
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        
        // Focus the confirm button
        document.getElementById('confirm-term-btn').focus();
    }

    /**
     * Clean up confirmation dialog and associated event listeners
     * @param {HTMLElement} confirmDialog - The dialog element to clean up
     */
    cleanupConfirmationDialog(confirmDialog) {
        console.log('[ManualTermsService] Cleaning up confirmation dialog');
        
        // Remove dialog from DOM if it exists
        if (confirmDialog && document.body.contains(confirmDialog)) {
            document.body.removeChild(confirmDialog);
        }
        
        // Remove outside click handler if it exists
        if (this.handleOutsideClick) {
            document.removeEventListener('mousedown', this.handleOutsideClick);
            this.handleOutsideClick = null;
        }
        
        // Remove scroll handlers if they exist
        if (this.handleScroll) {
            const claimsEditor = document.querySelector('.cm-editor .cm-scroller');
            if (claimsEditor) {
                claimsEditor.removeEventListener('scroll', this.handleScroll);
            }
            window.removeEventListener('scroll', this.handleScroll);
            this.handleScroll = null;
        }
        
        // Clear text selection
        if (window.getSelection) {
            window.getSelection().removeAllRanges();
        } else if (document.selection) {
            document.selection.empty(); // For older IE
        }
    }

    /**
     * Simple debounce function to prevent multiple rapid selections
     * @param {Function} func - Function to debounce
     * @param {number} wait - Wait time in milliseconds
     * @returns {Function} - Debounced function
     */
    debounce(func, wait) {
        return (...args) => {
            clearTimeout(this.selectionTimeout);
            this.selectionTimeout = setTimeout(() => {
                func.apply(this, args);
            }, wait);
        };
    }

    /**
     * Clean up resources
     */
    destroy() {
        if (this.unsubscribe) {
            this.unsubscribe();
            this.unsubscribe = null;
        }
        
        this.disableTermSelection();
        
        // Remove any confirmation dialog if it exists
        const confirmDialog = document.getElementById('term-confirmation-dialog');
        if (confirmDialog) {
            this.cleanupConfirmationDialog(confirmDialog);
        }
        
        if (this.selectionTimeout) {
            clearTimeout(this.selectionTimeout);
            this.selectionTimeout = null;
        }
    }
    
    /**
     * Get current claims content from store
     */
    getClaimsContent() {
        let content = '';
        const unsubscribe = this.claimsStore.subscribe(state => {
            content = state.content || '';
        });
        unsubscribe();
        return content;
    }
    
}